import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, ArrowUpRight } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { goCustomer } from "@/lib/links";
import { Logo } from "@/components/landing/Logo";
import { useBusinessCta } from "@/lib/useBusinessCta";

const NAV = [
  { label: "Discover", href: "#consumers" },
  { label: "For Business", href: "#businesses" },
  { label: "About", href: "#about" },
  { label: "Foundation", href: "#foundation" },
  { label: "Gallery", href: "#gallery" },
  { label: "Categories", href: "#categories" },
];

export default function Header() {
  const navigate = useNavigate();
  const { ready: businessCtaReady, onClick: handleBusinessClick } = useBusinessCta();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[#F7F5F2]/80 backdrop-blur-2xl border-b border-black/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 h-20 flex items-center justify-between">
        {/* Brand */}
        <button
          data-testid="brand-logo"
          onClick={() => navigate("/")}
          className="flex items-center gap-3"
        >
          <Logo size={46} className="shrink-0" />
          <span className="font-serif text-2xl md:text-[1.6rem] font-semibold tracking-tight text-[#2A2724] leading-none">
            Book Away
          </span>
        </button>

        {/* Center nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((item) => (
            <button
              key={item.label}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => scrollTo(item.href)}
              className="link-underline text-sm text-[#6B6B6B] hover:text-[#2A2724] transition-colors duration-300"
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* CTAs */}
        <div className="hidden md:flex items-center gap-3">
          <button
            data-testid="header-businesses-btn"
            onClick={handleBusinessClick}
            disabled={!businessCtaReady}
            aria-busy={!businessCtaReady}
            className="group inline-flex items-center gap-1.5 rounded-full border border-black/20 px-6 py-2.5 text-sm text-[#2A2724] hover:border-gold hover:text-gold transition-colors duration-300 disabled:cursor-progress disabled:opacity-70"
          >
            I'm a Business
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="header-customers-btn"
            onClick={() => goCustomer()}
            className="rounded-full bg-gold px-6 py-2.5 text-sm font-medium text-black hover:bg-[#AA8627] transition-colors duration-300"
          >
            I'm a Customer
          </button>
        </div>

        {/* Mobile menu */}
        <div className="md:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className="rounded-full border border-black/20 p-2.5 text-[#2A2724]"
              >
                <Menu className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 bg-[#F7F5F2] border-black/10 text-[#2A2724]"
            >
              {NAV.map((item) => (
                <DropdownMenuItem
                  key={item.label}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => scrollTo(item.href)}
                  className="cursor-pointer focus:bg-black/5 focus:text-gold"
                >
                  {item.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem
                data-testid="mobile-businesses-btn"
                onClick={handleBusinessClick}
                disabled={!businessCtaReady}
                className="cursor-pointer focus:bg-black/5 focus:text-gold"
              >
                I'm a Business →
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="mobile-customers-btn"
                onClick={() => goCustomer()}
                className="cursor-pointer text-gold focus:bg-black/5"
              >
                I'm a Customer →
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </motion.header>
  );
}
